import client from 'axios';

/**
 * The axios instance is built HERE from the package, not imported from
 * `Plugins/Axios` — which is what `store/User.js` does, and for the same
 * reason.
 *
 * `Plugins/Axios` reads `store.getters['User/isAuth']` at module scope and
 * imports the store to do it. A store module that imports it therefore closes a
 * cycle: store/index → store/Claim → Plugins/Axios → store/index (still
 * evaluating, so `undefined`) → `Cannot read properties of undefined (reading
 * 'getters')`, thrown before Vue mounts. The whole admin app rendered a blank
 * page. Found the first time it was opened in a browser; no build step catches
 * it, because the cycle is only a problem at runtime.
 */
const http = client.create({
    baseURL: process.env.VUE_APP_BASE_API_URL_HTTP,
});

/**
 * `Localization` reads the `Locale` HEADER and accepts only `en`/`lo`/`zh`. The
 * app stores `Lang=LA|CN` and sends it as a `lang` query param, which that
 * middleware ignores — so without this every label came back English on a Lao
 * screen. ⚠️ `la` is a hard 403: the backend maps `lo` onto its internal `la`
 * only AFTER the check.
 */
const LOCALE_BY_APP_LANG = { LA: 'lo', CN: 'zh', EN: 'en' };

/**
 * The claim line's published configuration.
 *
 * Everything here comes from `GET /api/claims/config` and NOTHING is restated in
 * this app. That is the point: the sign-off bands and the branch liability cap
 * live in the backend's config precisely so confirming them is an env change
 * rather than a deploy, and baking 1,000,000 / 10,000,000 into a Vue file throws
 * that away. The fifteen status labels come from there too, so this app and the
 * branch app and the customer app cannot describe the same claim differently.
 *
 * Fetched ONCE per session — it is read on every screen and must not become a
 * request per view. `loaded` guards the second caller; `loading` guards the
 * simultaneous ones.
 *
 * ⚠️ `normalAxios` (`$http`, baseURL `…/api/`), NOT `adminAxios` — the claim line
 * is `/api/claims`, not `/api/admin/...`. The legacy injured-parcel screens use
 * the same instance for the same reason.
 */
export default {
    namespaced: true,

    state: {
        config: null,
        loaded: false,
        loading: false,
    },

    getters: {
        config: (state) => state.config,
        isLoaded: (state) => state.loaded,

        /** `[{value, label}]` for a form's select. */
        options: (state) => (group) => (state.config && state.config[group]) || [],

        /** The words for one internal status, in the caller's language. */
        statusLabel: (state) => (value) => {
            const found = ((state.config && state.config.statuses && state.config.statuses.staff) || [])
                .find((s) => s.value === value);
            return found ? found.label : value;
        },

        /**
         * What a claim of several parcels CAME TO, when its parcels do not agree.
         *
         * Published beside the statuses because none of the six status words can describe a claim
         * with one box paid and another refused without being false about half of it. Falls back to
         * the raw value, like every other label here, so an outcome the server learns before this
         * app does still reads as something.
         */
        outcomeLabel: (state) => (value) => {
            const found = ((state.config && state.config.outcomes) || []).find((o) => o.value === value);
            return found ? found.label : value;
        },

        /**
         * What the claimant is to the BILL — its sender, its receiver, or both.
         *
         * ⚠️ Returns EMPTY for a value the config does not carry, unlike `statusLabel` and its
         * neighbours, which fall back to the raw code. Those describe something that happened and a
         * raw word still reads as something; this one would print `receiver` at an officer, which is
         * a client that restated the vocabulary badly rather than one that read it.
         *
         * Empty is also the right answer for a claim that HAS no side — one whose parcels disagree,
         * or one filed before the side was recorded. Both are "nothing to say here", and the screens
         * render nothing rather than a blank chip.
         */
        claimantRoleLabel: (state) => (value) => {
            const found = ((state.config && state.config.claimant_role) || []).find((r) => r.value === value);
            return found ? found.label : '';
        },

        /**
         * Who KEYED THE CLAIM IN — the customer themselves, or the office.
         *
         * A different question from `claimantRoleLabel` above, and the pair must never be collapsed:
         * a claim the office opened for a receiver is both, and a screen showing only one of them
         * has lost either who gets paid or whether anybody spoke to the claimant.
         */
        intakeChannelLabel: (state) => (value) => {
            const found = ((state.config && state.config.intake_channel) || []).find((c) => c.value === value);
            return found ? found.label : '';
        },

        /**
         * The words for what someone DID, from `/config` like everything else.
         * Falls back to the raw value so an action the server learns before this
         * app does still reads as something rather than as nothing.
         */
        actionLabel: (state) => (value) => {
            const found = ((state.config && state.config.actions) || []).find((a) => a.value === value);
            return found ? found.label : value;
        },

        /**
         * What the SERVERS accept, which is smaller than what the app allows.
         *
         * The 1 MB fallback is a restated value and deliberate: it is nginx's
         * OWN default, and it errs in the only safe direction. Guessing LOW
         * over-compresses a photograph, which costs a little detail; guessing
         * HIGH sails past `client_max_body_size` into a bare HTML 413 that no
         * client can parse, so the upload just fails with nothing on screen.
         * Program task 4.4.
         */
        maxFileBytes: (state) =>
            (state.config && state.config.intake && state.config.intake.max_file_bytes) || 1048576,

    },

    mutations: {
        setConfig(state, value) {
            state.config = value;
            state.loaded = true;
            state.loading = false;
        },
        setLoading(state, value) {
            state.loading = value;
        },
    },

    actions: {
        /** Idempotent: later callers get the cached copy, not a second request. */
        async load({ state, commit }) {
            if (state.loaded || state.loading) {
                return state.config;
            }

            commit('setLoading', true);

            try {
                // Read at CALL time, not at module load — by then the store
                // exists and the user has signed in. `access_token` is the key
                // `store/User.js` seeds its own state from.
                const token = window.localStorage.getItem('access_token');

                const res = await http.get('claims/config', {
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        Locale: LOCALE_BY_APP_LANG[window.localStorage.getItem('Lang')] || 'lo',
                    },
                });
                commit('setConfig', res.data.data);
                return state.config;
            } catch (e) {
                commit('setLoading', false);
                throw e;
            }
        },
    },
};
